import { PROXY } from "../lib/utils";
import useAuth from "../hooks/useAuth";
import { toast } from "react-toastify";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaRegThumbsUp, FaThumbsUp } from "react-icons/fa6";
import { readPendingCourseAction, takePendingLike } from "../lib/pendingCourseAction";

const Like = ({ mutate, likes, likesCount, courseId, onLoginRequired }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [likedStatus, setLikedStatus] = useState(null);
  const [statusReady, setStatusReady] = useState(false);
  const [confirmedCount, setConfirmedCount] = useState(null);
  const { user, isAuth, accessToken } = useAuth();
  const { t } = useTranslation();
  const normalizedLikes = Array.isArray(likes) ? likes : [];
  const userId = user?._id || user?.id;
  const hasLiked = likedStatus ?? normalizedLikes.some((like) => String(like?._id || like) === String(userId));
  const displayedCount = confirmedCount ?? likesCount ?? normalizedLikes.length;

  useEffect(() => {
    if (!isAuth || !accessToken || !courseId) {
      setLikedStatus(null);
      setStatusReady(false);
      return undefined;
    }
    const controller = new AbortController();
    setStatusReady(false);
    fetch(`${PROXY}/courses/like/${courseId}`, {
      headers: { Authorization: `Bearer ${accessToken}` }, signal: controller.signal,
    }).then((response) => {
      if (!response.ok) throw new Error("like_status_failed");
      return response.json();
    }).then((result) => {
      if (controller.signal.aborted) return;
      setLikedStatus(result.liked === true);
      setStatusReady(true);
    }).catch(() => {
      if (controller.signal.aborted) return;
      if (readPendingCourseAction()?.courseId === String(courseId) &&
          readPendingCourseAction()?.action === "like") {
        takePendingLike(String(courseId));
        toast.error(t("courseGuest.likeFailed"));
      }
    });
    return () => controller.abort();
  }, [isAuth, accessToken, courseId, t]);

  const updateLike = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${PROXY}/courses/like/${courseId}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) throw new Error("like_failed");
      const result = await response.json();
      setLikedStatus(result.liked === true);
      if (Number.isFinite(result.likesCount)) setConfirmedCount(result.likesCount);
      try { await mutate(); } catch { /* The confirmed response still updates this control. */ }
    } finally { setIsLoading(false); }
  }, [accessToken, courseId, mutate]);

  useEffect(() => {
    if (!isAuth || !accessToken || !courseId || !statusReady) return;
    const pending = readPendingCourseAction();
    if (pending?.action !== "like" || pending.courseId !== String(courseId)) return;
    // Consume before the request so remounts and retries cannot toggle the like twice.
    if (!takePendingLike(String(courseId))) return;
    if (hasLiked) return;
    updateLike().then(() => toast.success(t("courseGuest.likeSaved")))
      .catch(() => toast.error(t("courseGuest.likeFailed")));
  }, [isAuth, accessToken, courseId, statusReady, hasLiked, updateLike, t]);

  const likeHandler = async () => {
    if (!isAuth) {
      onLoginRequired?.("like");
      return;
    }
    try { await updateLike(); }
    catch { toast.error(t("courseGuest.likeFailed")); }
  };

  return (
    <div className="flex items-center justify-center gap-3 rounded-md px-1 shadow">
      <button type="button" disabled={isLoading} onClick={likeHandler} aria-pressed={hasLiked}
        aria-label={hasLiked ? t("Remove-like") : t("Like")}
        title={hasLiked ? t("Remove-like") : t("Like")}
        className={`rounded p-1 focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 ${
          hasLiked ? "bg-blue-100 text-blue-800" : "text-blue-600 hover:bg-blue-50"
        }`}>
        {hasLiked ? <FaThumbsUp size={20} aria-hidden="true" /> : <FaRegThumbsUp size={20} aria-hidden="true" />}
      </button>
      {isLoading ? <span className="text-sm text-blue-600" role="status">…</span>
        : <span className="text-md">{displayedCount}</span>}
    </div>
  );
};

export default Like;
